import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  Router,
  ActivatedRoute,
  RoutesRecognized,
  NavigationEnd,
} from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, pairwise } from 'rxjs/operators';
import { User } from 'src/app/_models';
import { Batch } from 'src/app/_models/batch-number';
import { Nip } from 'src/app/_models/nip';
import { Operation } from 'src/app/_models/operation';
import { PackageType } from 'src/app/_models/package_type';
import { Partner } from 'src/app/_models/partner';
import { Product } from 'src/app/_models/product';
import { ProductType } from 'src/app/_models/product_type';
import { Status } from 'src/app/_models/status';
import { Suboperation } from 'src/app/_models/suboperation';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { MessService } from 'src/app/_services/mess-service.service';
import { NipService } from 'src/app/_services/nip.service';
import { PackageTypesService } from 'src/app/_services/package-types.service';
import { PartnersService } from 'src/app/_services/partners.service';
import { ProductTypesService } from 'src/app/_services/product-types.service';
import { ProductsService } from 'src/app/_services/products.service';
import { StatusService } from 'src/app/_services/status.service';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-nip-add',
  templateUrl: './nip-add.component.html',
})
export class NipAddComponent implements OnInit, OnDestroy {
  addNipForm: FormGroup;
  disableSave: boolean = true;

  allPackageTypes: PackageType[];

  allProductTypes: ProductType[];

  allPartners: {
    data: any[];
    pagination: any;
  } = {
    data: null,
    pagination: null,
  };

  products: {
    data: any[];
    pagination: any;
  } = {
    data: null,
    pagination: null,
  };

  selectedProduct: Product;
  selectedProductOperations: Operation[];

  allStatuses: Status[];

  batchNumber: Batch;

  currentUser: User;
  currentDate = formatDate(new Date(), 'MM/dd/yyyy', 'en');

  isFromAddPage: boolean = false;

  savedFormData;

  createNipData;

  subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private messService: MessService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private nipService: NipService
  ) {}

  // * Lifecycles === //
  ngOnInit(): void {
    this.getPartners();
    this.getProducts();

    // * Get current user data
    this.currentUser = this.authService.userValue;

    // * Create form
    this.addNipForm = this.fb.group({
      batch: ['', Validators.required],
      creator_id: [this.currentUser.user.id, Validators.required],
      request_nr: ['', Validators.required],
      partner_id: ['', Validators.required],
      deadline: ['', Validators.required],
      packaging_type_id: ['', Validators.required],
      required_units: [''],
      planned_units: [''],
      status_id: ['', Validators.required],
      note: [''],
      complaint: [''],
      total_estimated_time: [{ value: '', disabled: true }],
      product_id: ['', Validators.required],
    });

    // * Get all data for form fields
    this.subscriptions.push(
      this.nipService.getCreateData().subscribe((createNipData) => {
        this.createNipData = createNipData;

        // Batch
        this.batchNumber = this.createNipData.batch;
        this.addNipForm.patchValue({
          batch: `${this.batchNumber}`,
        });

        // // Products
        // this.allProducts = createNipData.products;
        // this.allProducts = this.allProducts.filter(
        //   (product) => product.deleted_at == null
        // ); //* Show only products that have not been deleted

        // Package types
        this.allPackageTypes = createNipData.packaging_types;

        // // Partners
        // this.allPartners = createNipData.partners;

        // Statuses
        this.allStatuses = createNipData.statuses;

        // * Get previous url
        this.subscriptions.push(
          this.route.queryParams.subscribe((params) => {
            // Defaults to 0 if no query param provided.
            const previousUrl = params['fromPage'] || 0;
            const addedPartner = params['addedPartner'] || 0;
            this.isFromAddPage = previousUrl != 0 ? true : false;

            // * Proveri da li je korisnik sa neke ADD stranice
            this.checkIsUserFromAddPage();
            // if (addedPartner !== 0) {
            //   // Proverava da li je korisnik koji dolazi sa ADD stranice dodao Partnera,
            //   // ako jeste ubacuje ID tog partnera u formu
            //   this.addNipForm.patchValue({
            //     partner_id: this.allPartners.find(
            //       (partner) => partner.company == addedPartner
            //     ).id,
            //   });
            // }
          })
        );
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.messService.clearErrors();
  }

  getPartners(pageIndex = 1, searchTerm = ''): void {
    this.subscriptions.push(
      this.nipService.getPartners(pageIndex, 60, searchTerm).subscribe({
        next: (res: any) => {
          if (pageIndex === 1) {
            this.allPartners.data = [];
            this.allPartners.pagination = null;
          }

          this.allPartners.data = [...this.allPartners.data, ...res.data];
          this.allPartners.pagination = res?.pagination;
        },
        error: (err) => {},
      })
    );
  }

  getProducts(pageIndex = 1, searchTerm = ''): void {
    this.subscriptions.push(
      this.nipService.getProducts(pageIndex, 60, searchTerm).subscribe({
        next: (res: any) => {
          if (pageIndex === 1) {
            this.products.data = [];
            this.products.pagination = null;
          }

          this.products.data = [...this.products.data, ...res.data];
          this.products.pagination = res?.pagination;
        },
        error: (err) => {},
      })
    );
  }

  // * Add nip === //
  onAddNip() {
    const nip: Nip = this.addNipForm.getRawValue();
    let datePipe = new DatePipe('en-US');
    nip.deadline = datePipe.transform(nip.deadline, 'yyyy-MM-dd');
    this.subscriptions.push(
      this.nipService.addNip(nip).subscribe(
        () => {
          this.messService.add({
            severity: 'success',
            title: 'Uspešno kreiranje',
            detail: 'Kreirali ste novi NIP.',
          });
          this.router.navigate(['./../'], { relativeTo: this.route });
        },
        (error) => {
          const errorObj = error.error.errors;
          const form = this.addNipForm;
          const messService = this.messService;
          Object.keys(errorObj).forEach(function (key) {
            const formControl = form.get(key);
            if (formControl) {
              // If error exist set red border to field
              formControl.setErrors({});
            }
            // Push toast error for every field
            messService.addError({
              severity: 'error',
              title: 'Greška',
              detail: errorObj[key],
              sticky: true,
            });
          });
        }
      )
    );
  }

  // * Helpers === //
  getControls() {
    return (<FormGroup>this.addNipForm).controls;
  }

  // newProductSelected(event) {
  //   let selectedProductId = event.value;
  //   this.selectedProduct = this.allProducts.find(
  //     (product) => product.id === selectedProductId
  //   ); // Find product with selected ID
  //   this.selectedProductOperations = this.selectedProduct.operations;
  //   this.addNipForm.patchValue({
  //     total_estimated_time: this.selectedProduct.total_estimated_time,
  //   });
  // }

  // findSelectedPartner() {
  //   return this.allPartners.find(
  //     (partner) => partner.id == this.addNipForm.get('partner_id').value
  //   );
  // }

  // findSelectedProduct() {
  //   return this.allProducts.find(
  //     (product) => product.id == this.addNipForm.get('product_id').value
  //   );
  // }

  // * Check if user come from some ADD page (NIP etc.)
  private checkIsUserFromAddPage() {
    if (this.isFromAddPage) {
      this.subscriptions.push(
        this.nipService.saveFormData.subscribe((responseData) => {
          this.savedFormData = responseData;
        })
      );

      // Patch form with saved data
      if (this.savedFormData != null) {
        this.patchFormData();
      }
    }
  }

  private patchFormData() {
    this.addNipForm.patchValue({
      product_id: this.savedFormData.product_id,
      packaging_type_id: this.savedFormData.packaging_type_id,
      status_id: this.savedFormData.status_id,
      required_units: this.savedFormData.required_units,
      planned_units: this.savedFormData.planned_units,
      note: this.savedFormData.note,
      total_estimated_time: this.savedFormData.total_estimated_time,
      request_nr: this.savedFormData.request_nr,
      partner_id: this.savedFormData.partner_id,
      deadline: this.savedFormData.deadline,
    });
    // Update operation list
    // this.selectedProduct = this.savedFormData.product;
  }

  // * Emit form when user click add partners, to save data
  goToAddPage() {
    const nip = this.addNipForm.getRawValue();
    this.nipService.saveFormData.next(nip);
  }
}
